import React, { useEffect, useState } from "react";
import { State } from "../../redux/store";
import { useSelector } from "react-redux";
import { PaymentInterface, WorkYears } from "../payments/types";
import { DoctorInterface } from "../doctors/types";
import { useLocation } from "react-router-dom";
import { BsFiletypeXlsx } from "react-icons/bs";
import RenderTable from "./RenderTable"; // Make sure RenderTable is separated or below this code

import * as XLSX from "xlsx";

function exportToExcel(tableData: any, fileName: any) {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(tableData);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

const DataTableOrder = () => {
  const { payments } = useSelector((state: State) => state.payments);
  const { medecinTypes } = useSelector((state: State) => state.medecinType);
  const [typeMedecin, setTypeMedecin] = useState(medecinTypes[1]?.title);

  const [paymentsByDoctor, setPaymentsByDoctor] = useState<[]>([]);
  const location = useLocation();
  const currentTable = location.pathname.split("/")[2]; // "green", "yellow", "red"

  useEffect(() => {
    setPaymentsByDoctor(
      payments
        ?.filter(
          (payment: PaymentInterface) => !payment?.doctor?.status?.disappeared
        )
        ?.filter(
          (payment: PaymentInterface) =>
            payment?.doctor?.medecinType?.title === typeMedecin
        )
        .reduce((acc: any, payment: PaymentInterface) => {
          const doctorKey = `${payment.doctor?.RegNo}`;
          if (!acc[doctorKey]) acc[doctorKey] = [];
          acc[doctorKey].push(payment);
          return acc;
        }, {})
    );
  }, [payments, typeMedecin]);

  const currentYear = WorkYears[WorkYears.length - 1];

  const GreenTable: any = [];
  const YellowTable: any = [];
  const RedTable: any = [];

  Object.keys(paymentsByDoctor).forEach((doctorKey: any) => {
    const doctorPayments: any = paymentsByDoctor[doctorKey] || [];
    const doctorInfo: DoctorInterface = doctorPayments[0]?.doctor;
    const doctorInscriptionDate = new Date(
      doctorInfo.inscriptionDate
    ).getFullYear();

    const startDate = Math.max(doctorInscriptionDate, Number(WorkYears[0]));
    const yearsSliceStart = startDate - Number(WorkYears[0]);
    const yearsSliceEnd = Number(currentYear) - Number(WorkYears[0]) + 1;

    const filteredPayment = doctorPayments.filter(
      (payment: PaymentInterface) =>
        Number(payment.subscriptionYear) >= startDate &&
        WorkYears.slice(yearsSliceStart, yearsSliceEnd).includes(
          payment.subscriptionYear
        )
    );

    const yearsOfWork = WorkYears.slice(yearsSliceStart, yearsSliceEnd).length;
    const yearsNotPayed = yearsOfWork - filteredPayment.length;

    if (filteredPayment.length >= yearsOfWork) {
      GreenTable.push(doctorInfo);
    } else if (yearsNotPayed <= 2) {
      YellowTable.push({ ...doctorInfo, yearsNotPayed });
    } else {
      RedTable.push({ ...doctorInfo, yearsNotPayed });
    }
  });

  const tableColors: any = {
    green: {
      label: "Tableau Vert",
      bg: "bg-green-100",
      border: "border-l-green-500", // only left border
      button: "bg-green-600 hover:bg-green-700",
    },
    yellow: {
      label: "Tableau Jaune",
      bg: "bg-yellow-100",
      border: "border-l-yellow-500", // only left border
      button: "bg-yellow-600 hover:bg-yellow-700",
    },
    red: {
      label: "Tableau Rouge",
      bg: "bg-red-100",
      border: "border-l-red-500", // only left border
      button: "bg-red-600 hover:bg-red-700",
    },
  };

  const tableData =
    currentTable === "green"
      ? GreenTable
      : currentTable === "yellow"
      ? YellowTable
      : RedTable;

  return (
    <div
      className={`p-4 border-l-8 ${tableColors[currentTable]?.border} rounded-xl shadow-md`}
    >
      {/* Header section with Export + Type Filter */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {tableColors[currentTable]?.label}
        </h2>

        <button
          onClick={() =>
            exportToExcel(
              tableData.map((doctor: any) => ({
                Matricule:
                  doctor?.RegNo + "/" + (doctor?.medecinType?.title ?? ""),
                Nom: doctor?.fullName,
              })),
              `Tableau_${
                currentTable.charAt(0).toUpperCase() + currentTable.slice(1)
              }`
            )
          }
          className={`flex items-center gap-2 px-4 py-2 text-white font-semibold rounded-lg shadow ${tableColors[currentTable].button}`}
        >
          <BsFiletypeXlsx className="text-xl" />
          Exporter vers Excel
        </button>
      </div>

      {/* Table content */}
      <RenderTable
        tableType={currentTable}
        className={`rounded-lg p-2 ${tableColors[currentTable].bg}`}
        payments={tableData}
        medecinTypes={medecinTypes}
        typeMedecin={typeMedecin}
        setTypeMedecin={setTypeMedecin}
      />
    </div>
  );
};

export default DataTableOrder;
